import { useEffect, useState } from 'react';
import { ApiError, type AgentTemplateStatus, type Api } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';

/** Field paths of a template → the panel's own names for them (falls back to the path). */
const fieldName = (t: T, path: string) => (t(`templates.field.${path}`) === `templates.field.${path}` ? path : t(`templates.field.${path}`));
const nameOr = (t: T, key: string, fallback: string) => (t(key) === key ? fallback : t(key));

/**
 * ADR-038 on the agent page: which template (and version) it came from, a newer version with what it changes
 * — applied only when the business approves, its own edits kept — and "save as my template".
 */
export const TemplatePanel = ({ api, t, agentId, onChanged }: { api: Api; t: T; agentId: string; onChanged: () => void }) => {
  // ADR-071: applying an update and saving as a template need Agents; the origin and the update notice stay visible.
  const can = useCan();
  const canEdit = can(PERM.agents);
  const [status, setStatus] = useState<AgentTemplateStatus['template']>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', sector: '', structure: '', changelog: '' });

  // Industry and call-type choices come from the catalog (data), never from code.
  const [options, setOptions] = useState<{ sectors: string[]; structures: string[] }>({ sectors: [], structures: [] });
  const load = () => api.agentTemplate(agentId).then((r) => setStatus(r.template)).catch(() => setStatus(null));
  useEffect(() => void load(), [api, agentId]);
  useEffect(() => {
    const lang = document.documentElement.lang || 'en';
    api
      .templates({ lang })
      .then((r) => {
        const sectors = [...new Set(r.templates.map((x) => x.sector))];
        const structures = [...new Set(r.templates.map((x) => x.structure))];
        setOptions({ sectors, structures });
        setForm((f) => ({ ...f, sector: sectors.includes(f.sector) ? f.sector : (sectors[0] ?? ''), structure: structures.includes(f.structure) ? f.structure : (structures[0] ?? '') }));
      })
      .catch(() => undefined);
  }, [api]);

  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    setError(undefined);
    setDone(undefined);
    try {
      setDone(await fn());
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
    } finally {
      setBusy(false);
    }
  };

  const apply = () =>
    run(async () => {
      const r = await api.applyTemplateUpdate(agentId);
      return t('templates.update.done', { v: r.version, n: r.apply.length });
    });

  const save = () =>
    run(async () => {
      const r = await api.saveAsTemplate(agentId, { title: form.title.trim(), description: form.description.trim(), sector: form.sector, structure: form.structure, ...(form.changelog.trim() ? { changelog: form.changelog.trim() } : {}) });
      setSaving(false);
      return t('templates.save.done', { v: r.version });
    });

  if (status === undefined) return null;
  const up = status?.update;
  return (
    <div className="card template-panel">
      <div className="row-between">
        <div>
          <h3>{t('templates.panel.title')}</h3>
          {status ? (
            <p className="muted small">
              {status.icon} {nameOr(t, `sector.${status.sector}`, status.sector)} · {nameOr(t, `structure.${status.structure}`, status.structure)} · v{status.version}
              {status.owner === 'business' && <> · {t('templates.own')}</>}
            </p>
          ) : (
            <p className="muted small">{t('templates.panel.none')}</p>
          )}
        </div>
        {canEdit && <button type="button" className="secondary" disabled={busy} onClick={() => setSaving((x) => !x)}>{t('templates.save')}</button>}
      </div>

      {up && (
        <div className="banner info">
          <strong>{t('templates.update.title', { v: up.toVersion })}</strong>
          <ul className="small">{up.changelog.map((c) => <li key={c.version}>v{c.version}: {c.text || '—'}</li>)}</ul>
          {up.apply.length > 0 && <p className="small">{t('templates.update.apply', { list: up.apply.map((p) => fieldName(t, p)).join(', ') })}</p>}
          {up.conflicts.length > 0 && <p className="small">{t('templates.update.keep', { list: up.conflicts.map((p) => fieldName(t, p)).join(', ') })}</p>}
          <p className="hint">{t('templates.update.hint')}</p>
          {canEdit && <button type="button" className="primary" disabled={busy} onClick={() => void apply()}>{t('templates.update.button', { v: up.toVersion })}</button>}
        </div>
      )}

      {saving && canEdit && (
        <div className="form-grid one template-save">
          <p className="hint">{status?.owner === 'business' ? t('templates.save.newVersion') : t('templates.save.hint')}</p>
          <label>{t('templates.save.name')}<input maxLength={80} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
          <label>{t('templates.save.description')}<textarea rows={2} maxLength={300} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
          <label>{t('templates.sector')}
            <select value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })}>
              {options.sectors.map((x) => <option key={x} value={x}>{nameOr(t, `sector.${x}`, x)}</option>)}
            </select>
          </label>
          <label>{t('templates.structure')}
            <select value={form.structure} onChange={(e) => setForm({ ...form, structure: e.target.value })}>
              {options.structures.map((x) => <option key={x} value={x}>{nameOr(t, `structure.${x}`, x)}</option>)}
            </select>
          </label>
          {status?.owner === 'business' && (
            <label>{t('templates.save.changelog')}<input maxLength={500} value={form.changelog} onChange={(e) => setForm({ ...form, changelog: e.target.value })} /></label>
          )}
          <button type="button" className="primary" disabled={busy || !form.title.trim() || !form.sector || !form.structure} onClick={() => void save()}>{t('templates.save.button')}</button>
        </div>
      )}
      {done && <div className="banner ok small">{done}</div>}
      {error && <div className="banner error small">{t(`error.${error}`) === `error.${error}` ? error : t(`error.${error}`)}</div>}
    </div>
  );
};
